// "Simular vista empleado": dirección ve el ERP como lo vería un empleado
// concreto (menú, permisos de ruta, Mi Inicio con sus datos, Fichaje), en
// solo lectura. Se guarda en sessionStorage (se pierde al cerrar la pestaña)
// y solo vale si el usuario real es dirección: con cualquier otro usuario lo
// guardado se ignora. Cerrar sesión lo borra.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

export const CLAVE_VISTA_EMPLEADO = 'vista_empleado';
export const AVISO_SIMULACION = 'En la vista de simulación no se pueden hacer acciones';
// Alto (px) de la franja superior de la simulación (Layout): el contenido y el menú bajan lo mismo
export const ALTO_FRANJA_SIMULACION = 32;

const leerGuardada = () => {
  try {
    const texto = window.sessionStorage.getItem(CLAVE_VISTA_EMPLEADO);
    const valor = texto ? JSON.parse(texto) : null;
    return valor && valor.id ? valor : null;
  } catch (e) {
    return null;
  }
};

const guardar = (valor) => {
  try {
    if (valor) window.sessionStorage.setItem(CLAVE_VISTA_EMPLEADO, JSON.stringify(valor));
    else window.sessionStorage.removeItem(CLAVE_VISTA_EMPLEADO);
  } catch (e) {
    // Sin sessionStorage (modo privado): la simulación dura hasta recargar
  }
};

// Solo lo necesario del empleado (la ficha completa no hace falta aquí)
export const recortarEmpleado = (emp) => {
  if (!emp || !emp.id) return null;
  return {
    id: emp.id,
    nombre: emp.nombre || '',
    apellidos: emp.apellidos || '',
    email: emp.email || '',
    rol: emp.rol || '',
    permisos_modulos: Array.isArray(emp.permisos_modulos) ? emp.permisos_modulos : [],
    color: emp.color || null,
  };
};

export const VistaEmpleadoContext = createContext(null);

export const useVistaEmpleado = () => {
  const contexto = useContext(VistaEmpleadoContext);
  if (!contexto) {
    throw new Error('useVistaEmpleado debe usarse dentro de VistaEmpleadoProvider');
  }
  return contexto;
};

/**
 * - puedeVerRuta(pathname, usuario): la regla de rutas de App.js; al salir de
 *   la vista, si el usuario real no puede ver la ruta actual, vuelve a /dashboard.
 */
export const VistaEmpleadoProvider = ({ children, puedeVerRuta }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [guardada, setGuardada] = useState(leerGuardada);
  const esDireccion = user?.rol === 'direccion';

  // Sin sesión (cerrar sesión o caducada): fuera la simulación
  useEffect(() => {
    if (!user) {
      guardar(null);
      setGuardada(null);
    }
  }, [user]);

  const vistaEmpleado = esDireccion ? guardada : null;
  const simulando = !!vistaEmpleado;

  const simular = useCallback((empleado) => {
    if (!esDireccion) return;
    const valor = recortarEmpleado(empleado);
    guardar(valor);
    setGuardada(valor);
  }, [esDireccion]);

  const salir = useCallback(() => {
    guardar(null);
    setGuardada(null);
    if (puedeVerRuta && !puedeVerRuta(location.pathname, user)) {
      navigate('/dashboard', { replace: true });
    }
  }, [puedeVerRuta, location.pathname, user, navigate]);

  const usuarioEfectivo = useMemo(
    () => (simulando ? { ...vistaEmpleado, simulado: true } : user),
    [simulando, vistaEmpleado, user],
  );

  // puedeVerRuta se expone para que las pantallas decidan con la misma regla
  // de App.js (permisos finos y roles) qué enlaces enseñar a la persona efectiva
  const valor = useMemo(
    () => ({ vistaEmpleado, simular, salir, usuarioEfectivo, simulando, puedeVerRuta }),
    [vistaEmpleado, simular, salir, usuarioEfectivo, simulando, puedeVerRuta],
  );

  return <VistaEmpleadoContext.Provider value={valor}>{children}</VistaEmpleadoContext.Provider>;
};
